// #46 Kanban and KanbanCard: the Pipeline board. Columns are the seven DC-31 stages (Sourced · Qualified · In HM Review
// · Recruiter screen · HM interview · Final Interview Loop · Offer), each a .mc-kcol on --card-2 with its accent top
// rule (--mc-kc), a one-line header with the count, the cards as a list, and an optional foot. Held stays in its stage
// (a card pill); a Pass leaves the board for the *Passed* strip under it (the `passed` slot). `scroll`: the board
// scrolls sideways with snap and 280 px columns (every board does at ≤ 1180); a board wider than its box becomes a
// focusable, named region so the keyboard can scroll it. loading: skeleton cards at the card's geometry (aria-busy);
// an empty column prints one --text-3 line.
// A card is the candidate tile, the name (a stretched link when the card opens the candidate, so the whole card is
// the pointer target and the ring sits on the card), the now-line, pills (a node or an array), a decision stamp, the
// DC-97 aging line *In stage n days · Active Time* with the clock icon, facts, actions and, on Qualified cards, the selection CheckTile named by the
// candidate's name. A card that opens lifts on hover. A card passed with can={false} is left out before the list and
// the count, so a column never draws an empty item or counts a card the seat may not see.

import { Fragment, isValidElement, useId, type MouseEvent, type ReactNode } from "react";
import { Avatar, Count, Icon, Skeleton, type Blocked } from "../core";
import { HM_REVIEW_STATUS } from "../labels";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { useOverflow } from "../lib/useOverflow";
import { vars, type McVarValue } from "../lib/vars";
import { G6_COLUMN_LABELS, G6_TEMPLATES } from "./labels";
import { CheckTile } from "./Tile";

/** The seven stages of the board (DC-31), in order. */
export type PipelineStage = keyof typeof G6_COLUMN_LABELS;
export const PIPELINE_STAGES: readonly PipelineStage[] = [
  "sourced", "qualified", "inHmReview", "recruiterScreen", "hmInterview", "finalInterviewLoop", "offer",
];

/** Each stage's accent rule: the duty hue of where the candidate stands (DS §1.4). */
const STAGE_ACCENT: Readonly<Record<PipelineStage, McVarValue>> = {
  sourced: "var(--text-3)", qualified: "var(--blue)", inHmReview: "var(--blue)", recruiterScreen: "var(--purple)",
  hmInterview: "var(--purple)", finalInterviewLoop: "var(--purple)", offer: "var(--green)",
};

export interface KanbanColumn {
  /** One of the seven stages: its words and its accent. */
  stage?: PipelineStage | undefined;
  /** Words for a column that is not a stage (a label). */
  title?: string | undefined;
  /** The accent rule (a token var); default the stage's. */
  accent?: McVarValue | undefined;
  /** The cards (KanbanCard elements). */
  cards?: readonly ReactNode[] | undefined;
  /** The count in the header (default the cards). */
  count?: number | undefined;
  /** The line an empty column prints (default "—"). */
  empty?: ReactNode | undefined;
  /** The column's foot line (*1 Held*). */
  foot?: ReactNode | undefined;
  /** Two cards side by side (a widened column). */
  two?: boolean | undefined;
  hs?: Hs | undefined;
}

export interface KanbanProps {
  /** The board's name (a label: *Pipeline*); names the region when it scrolls. */
  label: string;
  columns: readonly KanbanColumn[];
  /** The board scrolls sideways with snap, 280 px columns. */
  scroll?: boolean | undefined;
  /** Columns run to the foot of their box (the full Pipeline page). */
  full?: boolean | undefined;
  /** Skeleton cards per column in place of the cards (true: 2). */
  loading?: boolean | number | undefined;
  /** The *Passed* strip under the board: rows of passed candidates with their disposition reasons. */
  passed?: ReactNode | undefined;
  /** The columns' heading level (default 3). */
  level?: 2 | 3 | 4 | undefined;
  className?: string | undefined;
}

function SkeletonCard() {
  return (
    <li className="mc-kc mc-kc-skel" aria-hidden="true">
      <span className="mc-kt"><Skeleton width={32} height={32} /><Skeleton width="70%" /></span>
      <Skeleton width="55%" height={12} />
    </li>
  );
}

function Column({ column, loading, level }: { column: KanbanColumn; loading: number; level: 2 | 3 | 4 }) {
  const headId = useId();
  const hot = useHs(column.hs);
  const words = column.title ?? (column.stage ? G6_COLUMN_LABELS[column.stage] : "");
  const cards = (column.cards ?? []).filter((card) => !(isValidElement<{ can?: boolean | undefined }>(card) && card.props.can === false));
  const accent = column.accent ?? (column.stage ? STAGE_ACCENT[column.stage] : undefined);
  const Heading = `h${level}` as const;
  let body: ReactNode;
  if (loading > 0) {
    body = <ul className="mc-klist">{Array.from({ length: loading }, (_, index) => <SkeletonCard key={index} />)}</ul>;
  } else if (cards.length === 0) {
    body = <p className="mc-kempty">{column.empty ?? "—"}</p>;
  } else {
    body = (
      <ul className={cx("mc-klist", column.two && "mc-kgrid")}>
        {cards.map((card, index) => <li key={index} className="mc-kli">{card}</li>)}
      </ul>
    );
  }
  return (
    <section className="mc-kcol" aria-labelledby={headId} style={vars({ "--mc-kc": accent })} {...hot}>
      <Heading className="mc-kh" id={headId}>
        <span className="mc-kn">{words}</span>
        {loading > 0 ? <Skeleton width={22} height={20} shape="pill" /> : <Count n={column.count ?? cards.length} />}
      </Heading>
      {body}
      {column.foot ? <div className="mc-kfoot">{column.foot}</div> : null}
    </section>
  );
}

export function Kanban({ label, columns, scroll, full, loading, passed, level = 3, className }: KanbanProps) {
  const PassedHeading = `h${level}` as const;
  const skeletons = loading === true ? 2 : typeof loading === "number" ? loading : 0;
  const passedId = useId();
  const { ref: board, overflows } = useOverflow<HTMLDivElement>();
  const region = Boolean(scroll) || overflows;
  return (
    <div className={cx("mc-kboard", className)}>
      <div
        ref={board}
        className={cx("mc-kanban", scroll && "mc-kanban-scroll", full && "mc-kanban-full")}
        style={vars({ "--mc-cols": columns.length })}
        role={region ? "region" : "group"}
        aria-label={label}
        tabIndex={region ? 0 : undefined}
        aria-busy={skeletons > 0 ? true : undefined}
      >
        {columns.map((column, index) => (
          <Column key={column.stage ?? `${column.title ?? ""}-${index}`} column={column} loading={skeletons} level={level} />
        ))}
      </div>
      {passed ? (
        <section className="mc-kpassed" aria-labelledby={passedId}>
          <PassedHeading className="mc-kpassed-h" id={passedId}>{HM_REVIEW_STATUS.passed}</PassedHeading>
          {passed}
        </section>
      ) : null}
    </div>
  );
}

export interface KanbanCardSelect {
  checked: boolean;
  onChange?: ((checked: boolean) => void) | undefined;
  blocked?: Blocked | undefined;
  hs?: Hs | undefined;
}

export interface KanbanCardProps {
  /** The candidate's name: the card's name and, with href/onOpen, its link. */
  name: string;
  /** The stable key the candidate tile's tint is taken from (the Candidate ID). */
  hueKey?: string | undefined;
  /** The tile's tint (a --t-* token var). */
  tint?: McVarValue | undefined;
  /** The line under the name (current role, the Recruiter's read). */
  now?: ReactNode | undefined;
  href?: string | undefined;
  onOpen?: ((event: MouseEvent<HTMLElement>) => void) | undefined;
  /** State pills (*In HM Review*, *Held*, *Overdue* …): one node, or an array drawn in order (empty entries left out). */
  pills?: ReactNode | readonly ReactNode[] | undefined;
  /** A decision stamp (a g3 Stamp: who decided and when) on its own line under the pills; reachable over the link. */
  stamp?: ReactNode | undefined;
  /** Days in this stage on Active Time: the aging line *In stage n days · Active Time*. */
  aging?: number | undefined;
  /** Fact lines (compensation alignment, location, notice). */
  facts?: ReactNode | undefined;
  /** Quick actions (icon buttons, a small Advance · Hold · Pass trio). */
  actions?: ReactNode | undefined;
  /** The selection tile (Qualified cards): ticking adds the candidate to the slate. */
  select?: KanbanCardSelect | undefined;
  /** false: the seat may not see this card. */
  can?: boolean | undefined;
  hs?: Hs | undefined;
  className?: string | undefined;
}

/** The card's pills as one list: an array keeps its order and drops null, undefined, false and empty strings. */
function pillList(pills: KanbanCardProps["pills"]): ReactNode {
  if (!Array.isArray(pills)) return pills === "" ? null : (pills as ReactNode);
  const shown = (pills as readonly ReactNode[]).filter((pill) => pill !== null && pill !== undefined && pill !== false && pill !== "");
  return shown.length ? shown.map((pill, i) => <Fragment key={i}>{pill}</Fragment>) : null;
}

export function KanbanCard({ name, hueKey, tint, now, href, onOpen, pills, stamp, aging, facts, actions, select, can, hs, className }: KanbanCardProps) {
  const nameId = useId();
  const hot = useHs(hs);
  if (can === false) return null;
  const pillRow = pillList(pills);
  const opens = href !== undefined || onOpen !== undefined;
  const title = !opens
    ? name
    : href !== undefined
      ? <a className="mc-kc-link" href={href} onClick={onOpen} {...hot}>{name}</a>
      : <button type="button" className="mc-kc-link" onClick={onOpen} {...hot}>{name}</button>;
  return (
    <article className={cx("mc-kc", opens && "mc-kc-open", select?.checked && "mc-kc-sel", className)} aria-labelledby={nameId}>
      <div className="mc-kt">
        <Avatar name={name} hueKey={hueKey} tint={tint} candidate decorative />
        <span className="mc-kpn">
          <b id={nameId}>{title}</b>
          {now !== undefined && now !== null ? <span>{now}</span> : null}
        </span>
        {select ? (
          <CheckTile plain checked={select.checked} onChange={select.onChange} blocked={select.blocked} hs={select.hs} labelledBy={nameId} className="mc-kc-tick" />
        ) : null}
      </div>
      {pillRow ? <div className="mc-km">{pillRow}</div> : null}
      {stamp !== undefined && stamp !== null && stamp !== false ? <div className="mc-km mc-kc-stamp">{stamp}</div> : null}
      {aging !== undefined ? (
        <div className="mc-km mc-age">
          <Icon name="clock" size={14} />
          <span>{G6_TEMPLATES.inStage({ n: aging })}</span>
        </div>
      ) : null}
      {facts ? <div className="mc-kfact">{facts}</div> : null}
      {actions ? <div className="mc-ka">{actions}</div> : null}
    </article>
  );
}
